import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { objectNotNullV2 } from '@/Utils/FnUtils';
import { apiClient } from '@/_config/lib/axios/api-client';
import { HEADER_TOTAL_COUNT } from '@/_config/lib/axios/interceptors';
import { handleResponseThrowError } from '@/_utils/services';
import type {
  DeliverymanByMission,
  GetMissionRankingParams,
  GetMissionRankingResponse,
} from '../types';

export const GET_MISSION_RANKING = 'GET_MISSION_RANKING';

const transformParams = ({
  deliverymanName,
  page,
  limit,
}: Omit<GetMissionRankingParams, 'missionId'>) => {
  const newPage = page || 1;
  const newLimit = limit || DEFAULT_LIMIT_REQUEST;
  const offset = (newPage - 1) * newLimit;

  return objectNotNullV2({
    deliverymanName: deliverymanName?.trim() || undefined,
    limit: newLimit,
    offset,
  });
};

const getMissionRanking = ({
  missionId,
  ...params
}: GetMissionRankingParams): Promise<GetMissionRankingResponse> => {
  const objParams = transformParams(params);

  return apiClient
    .get<{
      totalCount?: number;
      data: DeliverymanByMission[];
    }>(`/missions/${missionId}/ranking`, {
      params: objParams,
    })
    .then(response => {
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? [];

      const totalCountFromBody = Array.isArray(response.data)
        ? undefined
        : response.data?.totalCount;

      return {
        data,
        totalCount:
          Number(totalCountFromBody) ||
          Number(response.headers[HEADER_TOTAL_COUNT]) ||
          data.length,
      };
    })
    .catch(handleResponseThrowError());
};

type GetMissionRankingData = Awaited<ReturnType<typeof getMissionRanking>>;

export const useGetMissionRanking = (
  params: GetMissionRankingParams,
  options: Omit<
    UseQueryOptions<
      GetMissionRankingData,
      unknown,
      GetMissionRankingData,
      (string | GetMissionRankingParams)[]
    >,
    'queryKey' | 'queryFn'
  > = {},
) =>
  useQuery({
    queryKey: [GET_MISSION_RANKING, params],
    queryFn: () => getMissionRanking(params),
    enabled: !!params.missionId,
    ...options,
  });
